/**
 * API contracts for the `warehouse-core` module (M2) — zod schemas + inferred types
 * shared by apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the warehouse-core module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * Naming rule from the barrel: every exported symbol is prefixed with a module
 * noun (`warehouseArea…`, `bin…`, `pallet…`, `stocktake…`, `move…`) so two
 * modules can never collide on `createSchema`.
 */
import { z } from 'zod';

import { qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// enums (mirror db/schema/warehouse.ts)
// ---------------------------------------------------------------------------

export const warehouseAreaKindSchema = z.enum(['storage', 'floor']);
export type WarehouseAreaKind = z.infer<typeof warehouseAreaKindSchema>;

export const warehouseFloorRoleSchema = z.enum([
  'receiving',
  'packing',
  'dispatch',
  'production',
  'staging',
]);
export type WarehouseFloorRole = z.infer<typeof warehouseFloorRoleSchema>;

export const shelfDirectionSchema = z.enum(['normal', 'reversed']);
export type ShelfDirection = z.infer<typeof shelfDirectionSchema>;

export const shelfOrientationSchema = z.enum(['h', 'v']);
export type ShelfOrientation = z.infer<typeof shelfOrientationSchema>;

export const binKindSchema = z.enum(['bin', 'floor', 'pillar']);
export type BinKind = z.infer<typeof binKindSchema>;

export const palletStatusSchema = z.enum(['active', 'shipped', 'consumed', 'empty']);
export type PalletStatus = z.infer<typeof palletStatusSchema>;

export const stockMovementTypeSchema = z.enum([
  'receive',
  'putaway',
  'pick',
  'pack',
  'ship',
  'move',
  'adjust',
  'consume',
  'produce',
  'count',
  'palletize',
]);
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>;

export const stocktakeStatusSchema = z.enum(['draft', 'counting', 'review', 'posted']);
export type StocktakeStatus = z.infer<typeof stocktakeStatusSchema>;

export const stocktakeLineStatusSchema = z.enum(['open', 'counted', 'approved']);
export type StocktakeLineStatus = z.infer<typeof stocktakeLineStatusSchema>;

// ---------------------------------------------------------------------------
// bin code generation — ONE implementation, used by the api (authoritative) and
// by the web builder's live preview. Format `A-01-1A`:
// `<area code>-<shelf code>-<column no><level char>` (ARCHITECTURE §8.6).
// ---------------------------------------------------------------------------

/** `A` = level 1 … `F` = level 6. Levels above 26 are refused by the schema. */
export const BIN_LEVEL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const BIN_MAX_COLUMNS = 60;
export const BIN_MAX_LEVELS = 26;
/** Floor bins are `A-GOLV-1` — the Swedish word stays, it is printed on labels. */
export const FLOOR_BIN_SEGMENT = 'GOLV';

export function binLevelChar(level: number): string {
  const char = BIN_LEVEL_CHARS[level - 1];
  if (!char) throw new Error(`level out of range: ${level}`);
  return char;
}

export function binLevelNumber(char: string): number {
  return BIN_LEVEL_CHARS.indexOf(char.toUpperCase()) + 1;
}

/** `A`, `01`, 1, `A` → `A-01-1A`. */
export function buildBinCode(
  areaCode: string,
  shelfCode: string,
  columnNo: number,
  levelChar: string,
): string {
  return `${areaCode}-${shelfCode}-${columnNo}${levelChar}`;
}

export function buildFloorBinCode(areaCode: string, index: number): string {
  return `${areaCode}-${FLOOR_BIN_SEGMENT}-${index}`;
}

export interface GeneratedBin {
  code: string;
  columnNo: number;
  levelChar: string;
  /** Physical slot from the shelf's left/near end, 0-based. Grid position. */
  slot: number;
  level: number;
}

export interface BinGenerationInput {
  areaCode: string;
  shelfCode: string;
  columns: number;
  levels: number;
  direction: ShelfDirection;
}

/**
 * The bin codes a shelf owns, in physical order (slot 0 = the shelf's left/near
 * end). `direction: 'reversed'` numbers the columns from the OTHER end — that is
 * the whole point of the per-shelf setting (spec §5.15): two racks facing each
 * other must both count "up the aisle", so one of them counts right-to-left.
 *
 * Pure and total: same input, same codes, on the server and in the preview.
 */
export function generateBinCodes(input: BinGenerationInput): GeneratedBin[] {
  const { areaCode, shelfCode, columns, levels, direction } = input;
  const out: GeneratedBin[] = [];
  for (let slot = 0; slot < columns; slot += 1) {
    const columnNo = direction === 'reversed' ? columns - slot : slot + 1;
    for (let level = 1; level <= levels; level += 1) {
      const levelChar = binLevelChar(level);
      out.push({
        code: buildBinCode(areaCode, shelfCode, columnNo, levelChar),
        columnNo,
        levelChar,
        slot,
        level,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// areas
// ---------------------------------------------------------------------------

const areaCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(8)
  .regex(/^[A-Z0-9]+$/, 'area code must be A-Z / 0-9');

export const warehouseAreaSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  kind: warehouseAreaKindSchema,
  floorRole: warehouseFloorRoleSchema.nullable(),
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  sort: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type WarehouseArea = z.infer<typeof warehouseAreaSchema>;

export const warehouseAreaCreateSchema = z.object({
  code: areaCodeSchema,
  name: z.string().trim().min(1).max(120),
  kind: warehouseAreaKindSchema.default('storage'),
  floorRole: warehouseFloorRoleSchema.nullish(),
  x: z.number().int().min(0).max(500).default(0),
  y: z.number().int().min(0).max(500).default(0),
  w: z.number().int().min(1).max(500).default(4),
  h: z.number().int().min(1).max(500).default(4),
  sort: z.number().int().min(0).max(9999).default(0),
});
export type WarehouseAreaCreate = z.infer<typeof warehouseAreaCreateSchema>;

/**
 * `code` is deliberately absent: renaming an area would have to rewrite every
 * bin code under it (and every printed label). Bins are renamed through the
 * shelf-level regenerate path, which is audit-logged per bin.
 *
 * Written out rather than derived with `.partial()`: zod's `partial()` keeps the
 * `.default()` on each field, so a PATCH of `{ name }` would silently reset the
 * area's geometry to the create-defaults. A patch schema must have NO defaults.
 */
export const warehouseAreaUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    kind: warehouseAreaKindSchema.optional(),
    floorRole: warehouseFloorRoleSchema.nullish(),
    x: z.number().int().min(0).max(500).optional(),
    y: z.number().int().min(0).max(500).optional(),
    w: z.number().int().min(1).max(500).optional(),
    h: z.number().int().min(1).max(500).optional(),
    sort: z.number().int().min(0).max(9999).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty update' });
export type WarehouseAreaUpdate = z.infer<typeof warehouseAreaUpdateSchema>;

// ---------------------------------------------------------------------------
// shelves
// ---------------------------------------------------------------------------

const shelfCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(8)
  .regex(/^[A-Z0-9]+$/, 'shelf code must be A-Z / 0-9');

export const shelfSchema = z.object({
  id: uuidSchema,
  areaId: uuidSchema,
  code: z.string(),
  direction: shelfDirectionSchema,
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  orientation: shelfOrientationSchema,
  columns: z.number().int(),
  levels: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Shelf = z.infer<typeof shelfSchema>;

export const shelfCreateSchema = z.object({
  areaId: uuidSchema,
  code: shelfCodeSchema,
  direction: shelfDirectionSchema.default('normal'),
  x: z.number().int().min(0).max(500).default(0),
  y: z.number().int().min(0).max(500).default(0),
  w: z.number().int().min(1).max(500).default(1),
  h: z.number().int().min(1).max(500).default(1),
  orientation: shelfOrientationSchema.default('h'),
  columns: z.number().int().min(1).max(BIN_MAX_COLUMNS).default(1),
  levels: z.number().int().min(1).max(BIN_MAX_LEVELS).default(1),
});
export type ShelfCreate = z.infer<typeof shelfCreateSchema>;

/**
 * No defaults — see the note on `warehouseAreaUpdateSchema`. Here it matters
 * even more: a defaulted `columns: 1` on a PATCH that only flips `direction`
 * would silently delete every bin past the first column.
 */
export const shelfUpdateSchema = z
  .object({
    code: shelfCodeSchema.optional(),
    direction: shelfDirectionSchema.optional(),
    x: z.number().int().min(0).max(500).optional(),
    y: z.number().int().min(0).max(500).optional(),
    w: z.number().int().min(1).max(500).optional(),
    h: z.number().int().min(1).max(500).optional(),
    orientation: shelfOrientationSchema.optional(),
    columns: z.number().int().min(1).max(BIN_MAX_COLUMNS).optional(),
    levels: z.number().int().min(1).max(BIN_MAX_LEVELS).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty update' });
export type ShelfUpdate = z.infer<typeof shelfUpdateSchema>;

// ---------------------------------------------------------------------------
// bins
// ---------------------------------------------------------------------------

export const binSchema = z.object({
  id: uuidSchema,
  areaId: uuidSchema,
  shelfId: uuidSchema.nullable(),
  code: z.string(),
  columnNo: z.number().int().nullable(),
  levelChar: z.string().nullable(),
  kind: binKindSchema,
  isPickLocation: z.boolean(),
  isBlocked: z.boolean(),
  maxPallets: z.number().int().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Bin = z.infer<typeof binSchema>;

/** A bin row as the list/map endpoints return it: joined + with occupancy. */
export const binRowSchema = binSchema.extend({
  areaCode: z.string(),
  areaName: z.string(),
  shelfCode: z.string().nullable(),
  /** distinct (article, lot, pallet) quanta parked here */
  quantumCount: z.number().int(),
  palletCount: z.number().int(),
  totalQtyBase: z.string(),
});
export type BinRow = z.infer<typeof binRowSchema>;

/**
 * Manual bin creation — floor bins and pillars. Shelf bins are never created
 * one at a time: they are generated from the shelf so the codes cannot drift
 * from `columns`/`levels`/`direction`.
 */
export const binCreateSchema = z.object({
  areaId: uuidSchema,
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Z0-9-]+$/, 'bin code must be A-Z / 0-9 / -')
    .optional(),
  kind: z.enum(['floor', 'pillar']).default('floor'),
  isPickLocation: z.boolean().default(false),
  isBlocked: z.boolean().default(false),
  maxPallets: z.number().int().min(1).max(999).nullish(),
});
export type BinCreate = z.infer<typeof binCreateSchema>;

export const binUpdateSchema = z
  .object({
    isPickLocation: z.boolean(),
    isBlocked: z.boolean(),
    maxPallets: z.number().int().min(1).max(999).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'empty update' });
export type BinUpdate = z.infer<typeof binUpdateSchema>;

/** One line of a bin's contents (bin detail drawer, stocktaking bin flow). */
export const binContentSchema = z.object({
  stockId: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  palletId: uuidSchema.nullable(),
  lpn: z.string().nullable(),
  qtyBase: z.string(),
  unitCode: z.string().nullable(),
  /** Where it sits — redundant in a bin drawer, load-bearing on a pallet page. */
  binCode: z.string(),
});
export type BinContent = z.infer<typeof binContentSchema>;

export const binDetailSchema = binRowSchema.extend({
  contents: z.array(binContentSchema),
});
export type BinDetail = z.infer<typeof binDetailSchema>;

// ---------------------------------------------------------------------------
// the map payload (2D grid editor)
// ---------------------------------------------------------------------------

export const warehouseMapShelfSchema = shelfSchema.extend({
  binCount: z.number().int(),
  occupiedBinCount: z.number().int(),
});
export type WarehouseMapShelf = z.infer<typeof warehouseMapShelfSchema>;

export const warehouseMapAreaSchema = warehouseAreaSchema.extend({
  shelves: z.array(warehouseMapShelfSchema),
  /** floor bins + pillars only — shelf bins hang off their shelf */
  looseBins: z.array(binSchema),
  binCount: z.number().int(),
});
export type WarehouseMapArea = z.infer<typeof warehouseMapAreaSchema>;

export const warehouseMapSchema = z.object({
  areas: z.array(warehouseMapAreaSchema),
  /** grid extent, so the editor can size its canvas without measuring */
  width: z.number().int(),
  height: z.number().int(),
});
export type WarehouseMap = z.infer<typeof warehouseMapSchema>;

// ---------------------------------------------------------------------------
// stock views
// ---------------------------------------------------------------------------

export const stockRowSchema = z.object({
  id: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  unitCode: z.string().nullable(),
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  palletId: uuidSchema.nullable(),
  lpn: z.string().nullable(),
  binId: uuidSchema,
  binCode: z.string(),
  areaCode: z.string(),
  qtyBase: z.string(),
  updatedAt: z.iso.datetime(),
});
export type StockRow = z.infer<typeof stockRowSchema>;

/** `by-article` rollup: one row per article across the whole warehouse. */
export const stockByArticleRowSchema = z.object({
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  unitCode: z.string().nullable(),
  qtyBase: z.string(),
  binCount: z.number().int(),
  lotCount: z.number().int(),
  palletCount: z.number().int(),
  /** earliest best-before still on the floor — the FEFO warning column */
  earliestBestBefore: z.string().nullable(),
});
export type StockByArticleRow = z.infer<typeof stockByArticleRowSchema>;

export const stockMovementRowSchema = z.object({
  id: uuidSchema,
  at: z.iso.datetime(),
  type: stockMovementTypeSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  palletId: uuidSchema.nullable(),
  lpn: z.string().nullable(),
  fromBinId: uuidSchema.nullable(),
  fromBinCode: z.string().nullable(),
  toBinId: uuidSchema.nullable(),
  toBinCode: z.string().nullable(),
  qtyBase: z.string(),
  refKind: z.string().nullable(),
  refId: uuidSchema.nullable(),
  userId: uuidSchema.nullable(),
  userName: z.string().nullable(),
  deviceId: uuidSchema.nullable(),
  commandId: uuidSchema.nullable(),
  note: z.string().nullable(),
});
export type StockMovementRow = z.infer<typeof stockMovementRowSchema>;

// ---------------------------------------------------------------------------
// pallets & lots
// ---------------------------------------------------------------------------

export const palletRowSchema = z.object({
  id: uuidSchema,
  lpn: z.string(),
  ssccAlias: z.string().nullable(),
  status: palletStatusSchema,
  currentBinId: uuidSchema.nullable(),
  currentBinCode: z.string().nullable(),
  areaCode: z.string().nullable(),
  articleCount: z.number().int(),
  totalQtyBase: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PalletRow = z.infer<typeof palletRowSchema>;

export const palletDetailSchema = palletRowSchema.extend({
  contents: z.array(binContentSchema),
  history: z.array(stockMovementRowSchema),
});
export type PalletDetail = z.infer<typeof palletDetailSchema>;

/**
 * `pallet.create` — register a pallet the DEVICE minted (ARCHITECTURE §6).
 *
 * The scanner leases an `LP1#######` block up front so it can label a pallet at
 * the dock with no network; the server namespace is `LP0#######`, so the two can
 * never collide. Both the id and the LPN therefore arrive from the device and
 * are honoured verbatim — inventing new ones server-side would make the label
 * already stuck on the pallet a lie, and would orphan every queued command that
 * references the device's id.
 *
 * Re-sending the same pallet is a no-op that returns the existing row: the
 * receive flow legitimately emits `pallet.create` again after a reinstall, and
 * an offline outbox is replayed, not de-duplicated by hand.
 */
export const palletCreatePayloadSchema = z.object({
  /** Device-minted uuid. The pallet is created WITH this id. */
  palletId: uuidSchema,
  /** `LP1#######` from the device lease. */
  lpn: z.string().trim().min(3).max(20),
  /** Supplier SSCC captured as an alias — never a relabel (ARCHITECTURE §8.1). */
  ssccAlias: z.string().trim().min(8).max(30).nullish(),
  /** Wire alias for `ssccAlias`. */
  sscc: z.string().trim().min(8).max(30).nullish(),
  /** Where it was built. Optional: a pallet may exist before it has a location. */
  binId: uuidSchema.nullish(),
  binCode: z.string().trim().min(1).max(40).nullish(),
  /** `receive` | `palletize` | … — provenance for the audit trail. */
  createdFrom: z.string().trim().max(40).nullish(),
});
export type PalletCreatePayload = z.infer<typeof palletCreatePayloadSchema>;

export const palletCreateResultSchema = z.object({
  palletId: uuidSchema,
  lpn: z.string(),
  ssccAlias: z.string().nullable(),
  binId: uuidSchema.nullable(),
  /** false when the pallet already existed (a replayed outbox row). */
  created: z.boolean(),
});
export type PalletCreateResult = z.infer<typeof palletCreateResultSchema>;

export const lotRowSchema = z.object({
  id: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  lotNo: z.string(),
  bestBefore: z.string().nullable(),
  supplierId: uuidSchema.nullable(),
  supplierName: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  qtyBase: z.string(),
  binCount: z.number().int(),
});
export type LotRow = z.infer<typeof lotRowSchema>;

export const lotDetailSchema = lotRowSchema.extend({
  contents: z.array(binContentSchema),
  history: z.array(stockMovementRowSchema),
});
export type LotDetail = z.infer<typeof lotDetailSchema>;

// ---------------------------------------------------------------------------
// internal movements — the two scanner commands (`move.pallet`, `move.goods`)
// ---------------------------------------------------------------------------

/**
 * Scan LPN **or** supplier SSCC and a target bin. `observedFromBinCode` is what
 * the operator saw: the server verifies it and rejects (`E_CONFLICT`) rather
 * than trusting it — never last-write-wins on stock (ARCHITECTURE §6).
 *
 * ## Wire aliases (Android ⇄ API parity, RECONCILIATION)
 *
 * `MovePalletFlow.command` in the scanner app names three fields differently
 * and adds two it considers refinements. Accepting them here is strictly
 * cheaper — and safer — than renaming them on the device, because the app's
 * spelling is the one printed in its own reducer tests and outbox rows:
 *
 * | scanner sends | means | handled as |
 * |---|---|---|
 * | `lpn` | the code that was read | alias of `palletCode` |
 * | `fromBinId` | where the device *believed* the pallet was | alias of `observedFromBinId` — an OBSERVATION, still verified |
 * | `fromBinCode` | ditto, as a code | alias of `observedFromBinCode` |
 * | `wholePallet: true` | "move the whole pallet" | accepted and ignored — that is the only thing this command does |
 * | `movementType: 'putaway'` | the receiving leg | accepted and ignored — the ledger types a pallet relocation `move`; the receiving-specific ledger type lives on `goods.putaway` |
 *
 * The explicit field wins whenever both are present.
 */
export const movePalletPayloadSchema = z
  .object({
    palletId: uuidSchema.optional(),
    /** LPN (`LP0…`/`LP1…`) or the pallet's SSCC alias — either resolves. */
    palletCode: z.string().trim().min(1).max(40).optional(),
    /** Wire alias for `palletCode` — what the scanner calls the code it read. */
    lpn: z.string().trim().min(1).max(40).optional(),
    toBinId: uuidSchema.optional(),
    toBinCode: z.string().trim().min(1).max(40).optional(),
    observedFromBinId: uuidSchema.nullish(),
    observedFromBinCode: z.string().trim().min(1).max(40).nullish(),
    /** Wire alias for `observedFromBinId`. */
    fromBinId: uuidSchema.nullish(),
    /** Wire alias for `observedFromBinCode`. */
    fromBinCode: z.string().trim().min(1).max(40).nullish(),
    /** Accepted and ignored — see the alias table above. */
    wholePallet: z.boolean().optional(),
    /** Accepted and ignored — see the alias table above. */
    movementType: z.string().trim().max(40).optional(),
    note: z.string().trim().max(500).nullish(),
  })
  .transform((v) => ({
    ...v,
    palletCode: v.palletCode ?? v.lpn,
    observedFromBinId: v.observedFromBinId ?? v.fromBinId,
    observedFromBinCode: v.observedFromBinCode ?? v.fromBinCode,
  }))
  .refine((v) => Boolean(v.palletId ?? v.palletCode), { message: 'pallet required' })
  .refine((v) => Boolean(v.toBinId ?? v.toBinCode), { message: 'target bin required' });
/**
 * The WIRE shape (`z.input`), not the parsed one: this is the type a caller —
 * the web form, an integration test fixture, the scanner's own JSON — has to
 * satisfy. Handlers work with the result of `.parse()`, where every alias has
 * already been folded onto its canonical field.
 */
export type MovePalletPayload = z.input<typeof movePalletPayloadSchema>;

export const movePalletResultSchema = z.object({
  palletId: uuidSchema,
  lpn: z.string(),
  fromBinId: uuidSchema.nullable(),
  toBinId: uuidSchema,
  toBinCode: z.string(),
  movementIds: z.array(uuidSchema),
  /** zero for an empty pallet — relocating one is legal and not an error */
  movedLines: z.number().int(),
});
export type MovePalletResult = z.infer<typeof movePalletResultSchema>;

/**
 * Move a quantity of one article (optionally one lot, optionally off one
 * pallet) between two bins.
 *
 * The quantum keeps its pallet association across the move: the frozen ledger
 * takes ONE `palletId` per movement, so "take 4 cartons off pallet X and leave
 * them loose in the pick face" is not expressible as a single ledger row. That
 * is a deliberate v1 limitation, not an oversight — see the module report.
 *
 * ## Wire aliases (Android ⇄ API parity, RECONCILIATION)
 *
 * `MoveGoodsFlow.command` sends `fromPalletId` next to `palletId` (it mirrors
 * the two deliberately) and, when the operator scanned a pallet as the TARGET,
 * a `toPalletId`. `fromPalletId` is accepted as an alias. `toPalletId` is
 * accepted and **healed away**: moving a quantum onto a different pallet is the
 * feature the ledger cannot express, so the handler performs the bin move and
 * reports `healed` with `{toPalletId: null}` rather than pretending.
 */
export const moveGoodsPayloadSchema = z
  .object({
    articleId: uuidSchema,
    lotId: uuidSchema.nullish(),
    palletId: uuidSchema.nullish(),
    /** Wire alias for `palletId` — the pallet the goods come OFF. */
    fromPalletId: uuidSchema.nullish(),
    /**
     * The pallet the operator scanned as a target. Recorded for the heal note;
     * the movement itself keeps the source pallet association.
     */
    toPalletId: uuidSchema.nullish(),
    fromBinId: uuidSchema.optional(),
    fromBinCode: z.string().trim().min(1).max(40).optional(),
    toBinId: uuidSchema.optional(),
    toBinCode: z.string().trim().min(1).max(40).optional(),
    qtyBase: qtySchema,
    note: z.string().trim().max(500).nullish(),
  })
  .transform((v) => ({ ...v, palletId: v.palletId ?? v.fromPalletId }))
  .refine((v) => Boolean(v.fromBinId ?? v.fromBinCode), { message: 'source bin required' })
  .refine((v) => Boolean(v.toBinId ?? v.toBinCode), { message: 'target bin required' });
/** The WIRE shape (`z.input`) — see {@link MovePalletPayload}. */
export type MoveGoodsPayload = z.input<typeof moveGoodsPayloadSchema>;

export const moveGoodsResultSchema = z.object({
  movementId: uuidSchema,
  articleId: uuidSchema,
  fromBinId: uuidSchema,
  toBinId: uuidSchema,
  qtyBase: z.string(),
});
export type MoveGoodsResult = z.infer<typeof moveGoodsResultSchema>;

// ---------------------------------------------------------------------------
// stocktaking
// ---------------------------------------------------------------------------

/**
 * What a stocktake covers. Empty scope = the whole warehouse. All three lists
 * are ANDed across dimensions and ORed within one (bins in area A OR B).
 *
 * There is deliberately no "include empty bins" flag: `stocktake_lines` requires
 * an `article_id`, so a bin holding nothing has no line to create. Stock the
 * system did not know about is reported the other way round — the counter posts
 * a `stocktake.count` for a bin+article and the server creates the line with
 * `expected_qty_base = 0`.
 */
export const stocktakeScopeSchema = z.object({
  areaIds: z.array(uuidSchema).max(200).default([]),
  binIds: z.array(uuidSchema).max(2000).default([]),
  articleIds: z.array(uuidSchema).max(2000).default([]),
});
export type StocktakeScope = z.infer<typeof stocktakeScopeSchema>;

export const stocktakeSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  status: stocktakeStatusSchema,
  scope: stocktakeScopeSchema.nullable(),
  createdBy: uuidSchema.nullable(),
  createdByName: z.string().nullable(),
  postedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  lineCount: z.number().int(),
  countedCount: z.number().int(),
  diffCount: z.number().int(),
});
export type Stocktake = z.infer<typeof stocktakeSchema>;

export const stocktakeCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  scope: stocktakeScopeSchema.optional(),
  /** snapshot the lines immediately and go straight to `counting` */
  startNow: z.boolean().default(true),
});
export type StocktakeCreate = z.infer<typeof stocktakeCreateSchema>;

export const stocktakeLineSchema = z.object({
  id: uuidSchema,
  stocktakeId: uuidSchema,
  binId: uuidSchema,
  binCode: z.string(),
  areaCode: z.string(),
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  unitCode: z.string().nullable(),
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  palletId: uuidSchema.nullable(),
  lpn: z.string().nullable(),
  expectedQtyBase: z.string(),
  countedQtyBase: z.string().nullable(),
  countedBy: uuidSchema.nullable(),
  countedByName: z.string().nullable(),
  countedAt: z.iso.datetime().nullable(),
  status: stocktakeLineStatusSchema,
  /** counted − expected, `null` until counted. The review screen sorts on it. */
  diffQtyBase: z.string().nullable(),
});
export type StocktakeLine = z.infer<typeof stocktakeLineSchema>;

/**
 * Record a count. A line that does not exist yet is CREATED here with its
 * expected qty snapshotted at that moment — that is how a counter reports stock
 * the system did not know about, without the tablet needing a second endpoint.
 */
export const stocktakeCountPayloadSchema = z.object({
  stocktakeId: uuidSchema,
  lineId: uuidSchema.optional(),
  binId: uuidSchema.optional(),
  binCode: z.string().trim().min(1).max(40).optional(),
  articleId: uuidSchema.optional(),
  lotId: uuidSchema.nullish(),
  palletId: uuidSchema.nullish(),
  countedQtyBase: qtySchema,
  /** what the tablet believed the expected qty was — verified, not trusted */
  observedExpectedQtyBase: qtySchema.nullish(),
});
export type StocktakeCountPayload = z.infer<typeof stocktakeCountPayloadSchema>;

export const stocktakeCountResultSchema = z.object({
  lineId: uuidSchema,
  stocktakeId: uuidSchema,
  expectedQtyBase: z.string(),
  countedQtyBase: z.string(),
  diffQtyBase: z.string(),
  created: z.boolean(),
});
export type StocktakeCountResult = z.infer<typeof stocktakeCountResultSchema>;

export const stocktakePostPayloadSchema = z.object({
  stocktakeId: uuidSchema,
  /** Post only these lines; omit for "every counted line". */
  lineIds: z.array(uuidSchema).max(5000).optional(),
  /**
   * Treat uncounted lines as counted-zero. Off by default: a bin nobody walked
   * to is not the same statement as "that bin is empty".
   */
  treatUncountedAsZero: z.boolean().default(false),
});
export type StocktakePostPayload = z.infer<typeof stocktakePostPayloadSchema>;

export const stocktakePostResultSchema = z.object({
  stocktakeId: uuidSchema,
  postedLines: z.number().int(),
  adjustedLines: z.number().int(),
  movementIds: z.array(uuidSchema),
  /** signed sum of every adjustment, base units */
  netQtyBase: z.string(),
});
export type StocktakePostResult = z.infer<typeof stocktakePostResultSchema>;

/** `erp_outbox` payload for a posted stocktake — BC `inv.adjust`. */
export const stocktakeInvAdjustSchema = z.object({
  stocktakeId: uuidSchema,
  stocktakeName: z.string(),
  postedAt: z.iso.datetime(),
  lines: z.array(
    z.object({
      articleId: uuidSchema,
      articleNo: z.string(),
      lotNo: z.string().nullable(),
      binCode: z.string(),
      expectedQtyBase: z.string(),
      countedQtyBase: z.string(),
      diffQtyBase: z.string(),
    }),
  ),
});
export type StocktakeInvAdjust = z.infer<typeof stocktakeInvAdjustSchema>;

// ---------------------------------------------------------------------------
// command types registered with the CommandBus
// ---------------------------------------------------------------------------

/**
 * The scanner-executable half of this module. Same handler for the web button
 * and for a command drained from an Android outbox via `POST /api/sync/commands`
 * (ARCHITECTURE §6) — there is exactly one implementation of "move a pallet".
 */
export const WAREHOUSE_COMMAND_TYPES = {
  movePallet: 'move.pallet',
  moveGoods: 'move.goods',
  /** The scanner registering a pallet it minted from its own LPN lease. */
  palletCreate: 'pallet.create',
  stocktakeCount: 'stocktake.count',
} as const;

export type WarehouseCommandType =
  (typeof WAREHOUSE_COMMAND_TYPES)[keyof typeof WAREHOUSE_COMMAND_TYPES];

/** Delta resources this module publishes to the Android reference cache. */
export const WAREHOUSE_DELTA_RESOURCES = {
  bins: 'bins',
  pallets: 'pallets',
  lots: 'lots',
  /**
   * Current-state quanta, ledger-derived so that a quantum DELETED at zero is
   * still visible to a scanner that already cached it — see
   * `warehouse-core/stock.delta.ts`.
   */
  stock: 'stock',
} as const;
